import axios, { AxiosError } from 'axios';

// Browser requests use the same-origin proxy to avoid exposing the API to CORS.
// REACT_APP_API_URL remains the upstream target used by the development/Nginx proxy.
const baseURL = (
	process.env.REACT_APP_API_PROXY_PATH ||
	process.env.REACT_APP_API_URL ||
	''
).replace(/\/$/, '');

const api = axios.create({
	baseURL,
	timeout: 30000,
});

export type ConversationChannel = 'whatsapp' | 'website';

export interface ConversationUserDetails {
	name?: string | null;
	email?: string | null;
	country?: string | null;
	company?: string | null;
	phone?: string | null;
	[key: string]: unknown;
}

export interface ConversationUser {
	user_id: string;
	wa_id: string | null;
	created_at: string | null;
	updated_at: string;
	message_count: number;
	consultant_mode: boolean;
	user_info?: boolean;
	user_details?: ConversationUserDetails | null;
	userdetails?: ConversationUserDetails | null;
	[key: string]: unknown;
}

export interface ConversationMessage {
	index: number;
	role: 'user' | 'bot' | 'consultant' | string;
	text: string;
	media_id?: string | null;
	filename?: string | null;
	mime_type?: string | null;
	caption?: string | null;
	expired?: boolean;
}

export interface ConversationTranscript {
	user_id: string;
	wa_id: string | null;
	channel: ConversationChannel;
	created_at: string | null;
	updated_at: string;
	message_count: number;
	consultant_mode: boolean;
	consultant_since: string | null;
	messages: ConversationMessage[];
	[key: string]: unknown;
}

interface ApiEnvelope<T> {
	status: string;
	payload: T;
	msg: string;
}

export interface ConversationUsersPage {
	total: number;
	channel: ConversationChannel;
	users: ConversationUser[];
}

export const CONVERSATION_USERS_PAGE_SIZE = 20;

function getErrorMessage(error: unknown, fallback: string) {
	if (!axios.isAxiosError(error)) {
		return fallback;
	}

	const axiosError = error as AxiosError<{
		msg?: string;
		detail?: string | Array<{ msg?: string }>;
	}>;
	const data = axiosError.response?.data;
	if (data?.msg) {
		return data.msg;
	}
	if (typeof data?.detail === 'string') {
		return data.detail;
	}
	const details = data?.detail;
	if (Array.isArray(details)) {
		return details
			.map(item => item.msg)
			.filter(Boolean)
			.join(', ');
	}
	if (axiosError.code === 'ECONNABORTED') {
		return 'The server took too long to respond.';
	}
	if (!axiosError.response) {
		return 'Could not reach the conversation service.';
	}

	return fallback;
}

function unwrap<T>(response: ApiEnvelope<T>, fallback: string): T {
	if (response.status.toLowerCase() !== 'success') {
		throw new Error(response.msg || fallback);
	}
	return response.payload;
}

function getUtcTime(value: string) {
	const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
	return new Date(hasTimezone ? value : `${value}Z`).getTime();
}

export async function getConversationUsers(
	channel: ConversationChannel,
	{
		limit = CONVERSATION_USERS_PAGE_SIZE,
		skip = 0,
	}: { limit?: number; skip?: number } = {},
): Promise<ConversationUsersPage> {
	try {
		const response = await api.get<ApiEnvelope<ConversationUsersPage>>(
			`/users/${channel}`,
			{
				params: { limit, skip, include_empty: false },
			},
		);
		const payload = unwrap(response.data, 'Could not load conversations.');
		return {
			...payload,
			users: [...payload.users].sort(
				(a, b) => getUtcTime(b.updated_at) - getUtcTime(a.updated_at),
			),
		};
	} catch (error) {
		if (error instanceof Error && !axios.isAxiosError(error)) {
			throw error;
		}
		throw new Error(
			getErrorMessage(error, 'Could not load conversations.'),
		);
	}
}

export async function getConversationTranscript(userId: string) {
	try {
		const response = await api.get<ApiEnvelope<ConversationTranscript>>(
			`/users/${encodeURIComponent(userId)}/chat`,
		);
		return unwrap(response.data, 'Could not load the conversation.');
	} catch (error) {
		if (error instanceof Error && !axios.isAxiosError(error)) {
			throw error;
		}
		throw new Error(
			getErrorMessage(error, 'Could not load the conversation.'),
		);
	}
}

export async function updateConsultantMode(userId: string, enabled: boolean) {
	try {
		const response = await api.post<
			ApiEnvelope<{ enabled: boolean; since: string | null }>
		>(`/consultant/${encodeURIComponent(userId)}/mode`, { enabled });
		return unwrap(response.data, 'Could not update consultant mode.');
	} catch (error) {
		if (error instanceof Error && !axios.isAxiosError(error)) {
			throw error;
		}
		throw new Error(
			getErrorMessage(error, 'Could not update consultant mode.'),
		);
	}
}

export async function sendConsultantMessage(userId: string, text: string) {
	try {
		const response = await api.post<ApiEnvelope<{ sent: string }>>(
			`/consultant/${encodeURIComponent(userId)}/message`,
			{ text },
		);
		return unwrap(response.data, 'Could not send the message.');
	} catch (error) {
		if (error instanceof Error && !axios.isAxiosError(error)) {
			throw error;
		}
		throw new Error(getErrorMessage(error, 'Could not send the message.'));
	}
}

export async function sendConsultantDocument(
	userId: string,
	file: File,
	caption = '',
) {
	const form = new FormData();
	form.append('file', file, file.name);
	form.append('caption', caption);

	try {
		const response = await api.post<ApiEnvelope<unknown>>(
			`/consultant/${encodeURIComponent(userId)}/document`,
			form,
		);
		return unwrap(response.data, 'Could not send the document.');
	} catch (error) {
		if (error instanceof Error && !axios.isAxiosError(error)) {
			throw error;
		}
		throw new Error(getErrorMessage(error, 'Could not send the document.'));
	}
}

export async function sendConsultantVoice(userId: string, file: File) {
	const form = new FormData();
	form.append('file', file, file.name);

	try {
		const response = await api.post<ApiEnvelope<unknown>>(
			`/consultant/${encodeURIComponent(userId)}/voice`,
			form,
		);
		return unwrap(response.data, 'Could not send the voice note.');
	} catch (error) {
		if (error instanceof Error && !axios.isAxiosError(error)) {
			throw error;
		}
		throw new Error(
			getErrorMessage(error, 'Could not send the voice note.'),
		);
	}
}

export async function getConversationMedia(mediaId: string, filename = '') {
	try {
		const response = await api.get<Blob>(
			`/consultant/media/${encodeURIComponent(mediaId)}`,
			{
				params: filename ? { filename } : undefined,
				responseType: 'blob',
			},
		);
		return response.data;
	} catch (error) {
		throw new Error(getErrorMessage(error, 'Could not download the file.'));
	}
}
